import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, fileUrl } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Loader2, ShoppingCart, ImageOff, Plus, Clock } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["All", "Parts", "Accessories", "Apparel", "Bundles", "Other"];
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const cart = useCart();

  useEffect(() => {
    api.get("/products")
      .then((res) => setProducts(res.data))
      .catch(() => toast.error("Failed to load shop"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = cat === "All" ? products : products.filter((p) => p.category === cat);

  return (
    <div className="min-h-screen bg-spark-floor">
      <Navbar />
      <header className="bg-spark-teal-deep text-white border-b-4 border-spark-yellow" data-testid="shop-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <p className="label-mono text-spark-yellow mb-2">Workshop store</p>
          <h1 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95]">
            Parts &amp; merch
          </h1>
          <p className="mt-4 text-slate-300 max-w-2xl">
            Genuine spares, upgrades, and Spark-branded gear. Pick up free from the workshop, or have it shipped NZ-wide.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-3.5 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold border transition-colors ${cat === c ? "bg-spark-teal-deep text-white border-spark-teal-deep" : "bg-white text-spark-slate border-spark-line hover:border-spark-teal hover:text-spark-teal"}`}
              data-testid={`shop-cat-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20"><Loader2 className="size-6 animate-spin inline text-spark-teal" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-16 text-spark-slate">Nothing here yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {filtered.map((p) => {
              const isOnOrder = p.availability === "on_order";
              const outOfStock = !isOnOrder && p.track_stock && p.stock === 0;
              const lead = p.lead_time_days || 7;
              return (
              <article key={p.id} className="surface-card overflow-hidden flex flex-col group" data-testid={`product-card-${p.sku}`}>
                <Link to={`/shop/p/${p.slug}`} className="block aspect-square bg-spark-floor relative overflow-hidden">
                  {p.images?.[0] ? (
                    <img src={fileUrl(p.images[0].storage_path)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-spark-line">
                      <ImageOff className="size-10" />
                    </div>
                  )}
                  {outOfStock && (
                    <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-spark-ink text-white text-[10px] uppercase tracking-wider font-bold">Out of stock</span>
                  )}
                  {isOnOrder && (
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-400 text-spark-ink text-[10px] uppercase tracking-wider font-bold shadow-md" data-testid={`badge-on-order-${p.sku}`}>
                      <Clock className="size-3" /> On order · ~{lead}d
                    </span>
                  )}
                </Link>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="label-mono text-spark-teal">{p.category}</p>
                  <Link to={`/shop/p/${p.slug}`} className="font-display uppercase tracking-tight text-base font-bold text-spark-ink mt-1 line-clamp-2 hover:text-spark-teal">
                    {p.name}
                  </Link>
                  <p className="font-display uppercase tracking-tight text-xl font-bold text-spark-ink mt-2">{money(p.price)}</p>
                  {isOnOrder && (
                    <p className="text-[11px] text-spark-slate mt-1">Ships in ~{lead} days</p>
                  )}
                  <button
                    onClick={() => { cart.add(p); toast.success(`Added · ${p.name}`); }}
                    disabled={outOfStock}
                    className="btn-primary mt-3 w-full disabled:opacity-40"
                    data-testid={`add-to-cart-${p.sku}`}
                  >
                    <Plus className="size-4" /> Add to cart
                  </button>
                </div>
              </article>
            );})}
          </div>
        )}
      </main>

      {cart.count > 0 && (
        <Link
          to="/shop/cart"
          className="fixed bottom-5 right-5 z-30 inline-flex items-center gap-2 bg-spark-yellow text-spark-ink font-display uppercase tracking-wider font-bold px-5 py-3 rounded-full shadow-2xl hover:bg-spark-yellow-dark animate-in fade-in slide-in-from-bottom-4"
          data-testid="floating-cart"
        >
          <ShoppingCart className="size-4" /> View cart · {cart.count} · {money(cart.subtotal)}
        </Link>
      )}

      <Footer />
    </div>
  );
};

export default Shop;
